"use client";

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ProductsService } from "@/services/ProductsService";
import { ProductAdditionalsService } from "@/services/ProductAdditionalsService";
import type {
  ProductAdditional,
  ProductAdditionalItem,
  ProductsOutput,
} from "@/interfaces";
import { numberToMoney } from "@/util/moneyConversion";
import {
  X,
  Plus,
  Minus,
  Package,
  Trash2,
  Sparkles,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { AdditionalSelectionType } from "@/interfaces/products";

// Generic product interface that works for both offers and orders
export interface UnifiedProductItem {
  product_id?: string; // For offers (snake_case)
  productId?: string; // For orders (camelCase)
  quantity: number;
  additionals?: UnifiedProductAdditional[];
}

export interface UnifiedProductAdditional {
  itemId: string;
  quantity: number;
}

interface UnifiedProductSelectorProps {
  value: UnifiedProductItem[];
  onChange: (products: UnifiedProductItem[]) => void;
  placeholder?: string;
  showBadges?: boolean;
  mode?: "offers" | "orders"; // Determines which field to use
  showFinancialInfo?: boolean; // Show price/cost information
  showMarginInfo?: boolean; // Show margin calculations
}

interface ProductAdditionalSummary {
  itemId: string;
  label: string;
  unitPrice: number;
  unitCost: number;
  quantity: number;
}

interface ProductDetails extends ProductsOutput {
  rowKey: string;
  rowIndex: number;
  quantity: number;
  additionals?: ProductAdditionalSummary[];
}

export default function UnifiedProductSelector({
  value,
  onChange,
  placeholder = "Selecione produtos",
  showBadges = true,
  mode = "orders",
  showFinancialInfo = true,
  showMarginInfo = false,
}: UnifiedProductSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [products, setProducts] = useState<ProductsOutput[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<ProductDetails[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [quantityInputs, setQuantityInputs] = useState<Record<string, string>>({});
  const [collapsedAdditionals, setCollapsedAdditionals] = useState<
    Record<string, boolean>
  >({});

  const productsServiceRef = useRef(new ProductsService());
  const productCacheRef = useRef<Map<string, ProductsOutput>>(new Map());
  const additionalsCacheRef = useRef<Map<string, ProductAdditional[]>>(new Map());
  const getProductId = useMemo(() => {
    return (item: UnifiedProductItem): string => {
      return mode === "offers"
        ? item.product_id || item.productId || ""
        : item.productId || item.product_id || "";
    };
  }, [mode]);

  // Helper function to create product item based on mode
  const createProductItem = useMemo(() => {
    return (productId: string, quantity: number): UnifiedProductItem => {
      return mode === "offers"
        ? { product_id: productId, quantity, additionals: [] }
        : { productId: productId, quantity, additionals: [] };
    };
  }, [mode]);

  useEffect(() => {
    let isSubscribed = true;

    const loadProducts = async () => {
      setLoading(true);
      try {
        const response = await productsServiceRef.current.findAll({
          page: 1,
          pageSize: 100,
          query: searchValue,
          expand: ["file"],
        });

        if (!isSubscribed) {
          return;
        }

        const dataWithCachedAdditionals = response.data.map((product) => {
          const cachedAdditionals = additionalsCacheRef.current.get(product.id);
          return cachedAdditionals
            ? { ...product, additional: cachedAdditionals }
            : product;
        });

        setProducts(dataWithCachedAdditionals);
      } catch (error) {
        console.error("Erro ao carregar produtos:", error);
      } finally {
        if (isSubscribed) {
          setLoading(false);
        }
      }
    };

    loadProducts();

    return () => {
      isSubscribed = false;
    };
  }, [searchValue]);

  const currentProductRows = useMemo(() => {
    return value
      .map((item, index) => {
        const productId = getProductId(item);
        return {
          productId,
          rowIndex: index,
          rowKey: `${productId}-${index}`,
          quantity: item.quantity,
        };
      })
      .filter((row): row is {
        productId: string;
        rowIndex: number;
        rowKey: string;
        quantity: number;
      } => Boolean(row.productId));
  }, [value, getProductId]);

  const currentProductIds = useMemo(() => {
    return Array.from(new Set(currentProductRows.map((row) => row.productId)));
  }, [currentProductRows]);

  useEffect(() => {
    setCollapsedAdditionals((prev) => {
      const next: Record<string, boolean> = {};
      currentProductRows.forEach(({ rowKey }) => {
        next[rowKey] = prev[rowKey] ?? false;
      });
      return next;
    });
  }, [currentProductRows]);

  const loadProductAdditionals = useCallback(async (
    productId: string,
  ): Promise<ProductAdditional[]> => {
    const additionalsCache = additionalsCacheRef.current;
    if (additionalsCache.has(productId)) {
      return additionalsCache.get(productId) ?? [];
    }

    try {
      const additionalsService = new ProductAdditionalsService(productId);
      const additionals = await additionalsService.list();
      additionalsCache.set(productId, additionals);
      setProducts((prevProducts) =>
        prevProducts.map((product) =>
          product.id === productId ? { ...product, additional: additionals } : product,
        ),
      );
      return additionals;
    } catch (error) {
      console.error(
        `Erro ao carregar adicionais do produto ${productId}:`,
        error,
      );
      additionalsCache.set(productId, []);
      return [];
    }
  }, []);

  const ensureProductInCache = useCallback(async (
    productId: string,
  ): Promise<ProductsOutput | null> => {
    if (!productId) {
      return null;
    }

    const cache = productCacheRef.current;
    let cachedProduct = cache.get(productId);

    if (!cachedProduct) {
      try {
        cachedProduct = await productsServiceRef.current.findById(productId, {
          expand: ["file"],
        });
        cache.set(productId, cachedProduct);
      } catch (error) {
        console.error(
          `Erro ao carregar detalhes do produto ${productId}:`,
          error,
        );
        return null;
      }
    }

    if (!additionalsCacheRef.current.has(productId)) {
      const additionals = await loadProductAdditionals(productId);
      cachedProduct = { ...cachedProduct, additional: additionals };
      cache.set(productId, cachedProduct);
    }

    return cache.get(productId) ?? null;
  }, [loadProductAdditionals]);

  const prefetchProductDetails = useCallback(
    (productId: string) => {
      void ensureProductInCache(productId);
    },
    [ensureProductInCache],
  );

  useEffect(() => {
    setQuantityInputs((prev) => {
      const next = { ...prev };
      let changed = false;
      const rowKeys = currentProductRows.map((row) => row.rowKey);

      currentProductRows.forEach(({ rowKey, quantity }) => {
        if (!(rowKey in next)) {
          next[rowKey] = String(quantity);
          changed = true;
        }
      });

      Object.keys(next).forEach((rowKey) => {
        if (!rowKeys.includes(rowKey)) {
          delete next[rowKey];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [currentProductRows]);

  useEffect(() => {
    let isSubscribed = true;

    const updateSelectedProducts = async () => {
      if (value.length === 0) {
        if (isSubscribed) {
          setSelectedProducts([]);
          productCacheRef.current.clear();
        }
        return;
      }

      try {
        await Promise.all(
          currentProductIds.map((productId) => ensureProductInCache(productId)),
        );
      } catch (error) {
        console.error("Erro ao preparar os produtos selecionados:", error);
      }

      if (!isSubscribed) {
        return;
      }

      const cache = productCacheRef.current;
      const productDetails: ProductDetails[] = [];

      for (const [index, item] of value.entries()) {
        const productId = getProductId(item);
        if (!productId) {
          continue;
        }

        const product = cache.get(productId);
        if (!product) {
          continue;
        }

        const additionalsSummary = (item.additionals ?? [])
          .map((additional) => {
            const additionalItem = product.additional
              ?.flatMap((group) => group.items)
              .find((groupItem) => groupItem.id === additional.itemId);

            if (!additionalItem) {
              return null;
            }

            return {
              itemId: additional.itemId,
              label: additionalItem.name,
              unitPrice: additionalItem.unitPrice,
              unitCost: additionalItem.unitCost,
              quantity: additional.quantity,
            } satisfies ProductAdditionalSummary;
          })
          .filter(Boolean) as ProductAdditionalSummary[];

        productDetails.push({
          ...product,
          rowKey: `${productId}-${index}`,
          rowIndex: index,
          quantity: item.quantity,
          additionals: additionalsSummary,
        });
      }

      if (isSubscribed) {
        setSelectedProducts(productDetails);
      }
    };

    updateSelectedProducts();

    return () => {
      isSubscribed = false;
    };
  }, [value, currentProductIds, getProductId, ensureProductInCache]);

  const handleSelectProduct = (product: ProductsOutput) => {
    const newProducts = [...value, createProductItem(product.id, 1)];
    const newRowKey = `${product.id}-${newProducts.length - 1}`;
    setQuantityInputs((prev) => ({
      ...prev,
      [newRowKey]: "1",
    }));
    onChange(newProducts);
    setOpen(false);
    prefetchProductDetails(product.id);
  };

  const handleQuantityChange = (
    rowIndex: number,
    rowKey: string,
    quantity: number
  ) => {
    if (quantity <= 0) {
      handleRemoveProduct(rowIndex, rowKey);
      return;
    }

    setQuantityInputs((prev) => ({
      ...prev,
      [rowKey]: String(quantity),
    }));

    const updatedProducts = value.map((productItem, index) => {
      return index === rowIndex ? { ...productItem, quantity } : productItem;
    });
    onChange(updatedProducts);
  };

  const handleRemoveProduct = (rowIndex: number, rowKey: string) => {
    setQuantityInputs((prev) => {
      if (!(rowKey in prev)) {
        return prev;
      }
      const updated = { ...prev };
      delete updated[rowKey];
      return updated;
    });
    setCollapsedAdditionals((prev) => {
      if (!(rowKey in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
    const updatedProducts = value.filter((_, index) => index !== rowIndex);
    onChange(updatedProducts);
  };

  const findProductValue = (rowIndex: number) => {
    return value[rowIndex];
  };

  const getAdditionalQuantity = (rowIndex: number, itemId: string) => {
    const product = findProductValue(rowIndex);
    return (
      product?.additionals?.find((additional) => additional.itemId === itemId)
        ?.quantity ?? 0
    );
  };

  const updateGroupAdditionals = (
    rowIndex: number,
    group: ProductAdditional,
    updatedItems: UnifiedProductAdditional[]
  ) => {
    if (!value[rowIndex]) {
      return;
    }

    const currentProduct = value[rowIndex];
    const groupItemIds = new Set(
      group.items.map((groupItem) => groupItem.id).filter(Boolean) as string[]
    );

    const filteredExistingAdditionals = (
      currentProduct.additionals ?? []
    ).filter((additional) => !groupItemIds.has(additional.itemId));

    const sanitizedUpdated = updatedItems.filter(
      (item) => item.itemId && item.quantity > 0
    );

    const updatedProduct: UnifiedProductItem = {
      ...currentProduct,
      additionals: [...filteredExistingAdditionals, ...sanitizedUpdated],
    };

    const updatedProducts = [...value];
    updatedProducts[rowIndex] = updatedProduct;
    onChange(updatedProducts);
  };

  const handleRadioSelection = (
    rowIndex: number,
    group: ProductAdditional,
    selectedItemId: string | null
  ) => {
    if (!selectedItemId) {
      updateGroupAdditionals(rowIndex, group, []);
      return;
    }

    updateGroupAdditionals(rowIndex, group, [
      { itemId: selectedItemId, quantity: Math.max(1, group.minQuantity || 1) },
    ]);
  };

  const handleCheckboxToggle = (
    rowIndex: number,
    group: ProductAdditional,
    item: ProductAdditionalItem,
    checked: boolean
  ) => {
    const currentSelection = getGroupSelection(rowIndex, group);
    const otherTotal = currentSelection
      .filter((selection) => selection.item.id !== item.id)
      .reduce((sum, selection) => sum + selection.quantity, 0);

    if (checked && group.maxQuantity > 0 && otherTotal + 1 > group.maxQuantity) {
      return;
    }

    const currentQuantity = checked ? Math.max(1, group.minQuantity || 1) : 0;

    const updatedGroupSelection = group.items.map((groupItem) => {
        const quantity =
          groupItem.id === item.id
            ? currentQuantity
            : getAdditionalQuantity(rowIndex, groupItem.id || "");
      return {
        itemId: groupItem.id || "",
        quantity,
      };
    });

    updateGroupAdditionals(rowIndex, group, updatedGroupSelection);
  };

  const handleNumberSelection = (
    rowIndex: number,
    group: ProductAdditional,
    item: ProductAdditionalItem,
    quantity: number
  ) => {
    const normalizedQuantity = Math.max(0, quantity);
    const currentSelection = getGroupSelection(rowIndex, group);
    const otherTotal = currentSelection
      .filter((selection) => selection.item.id !== item.id)
      .reduce((sum, selection) => sum + selection.quantity, 0);

    const availableSlots =
      group.maxQuantity && group.maxQuantity > 0
        ? Math.max(group.maxQuantity - otherTotal, 0)
        : undefined;

    const allowedQuantity =
      availableSlots !== undefined
        ? Math.min(normalizedQuantity, availableSlots)
        : normalizedQuantity;

    const updatedGroupSelection = group.items.map((groupItem) => {
      const nextQuantity =
        groupItem.id === item.id
          ? allowedQuantity
          : getAdditionalQuantity(rowIndex, groupItem.id || "");

      return {
        itemId: groupItem.id || "",
        quantity: nextQuantity,
      };
    });

    updateGroupAdditionals(rowIndex, group, updatedGroupSelection);
  };

  const getGroupSelection = (
    rowIndex: number,
    group: ProductAdditional
  ): { item: ProductAdditionalItem; quantity: number }[] => {
    return group.items.map((item) => ({
      item,
      quantity: getAdditionalQuantity(rowIndex, item.id || ""),
    }));
  };

  const getGroupSelectionTotal = (
    rowIndex: number,
    group: ProductAdditional
  ) => {
    return getGroupSelection(rowIndex, group).reduce(
      (sum, selection) => sum + selection.quantity,
      0
    );
  };

  const isGroupSelectionInvalid = (
    rowIndex: number,
    group: ProductAdditional
  ) => {
    const totalSelected = getGroupSelectionTotal(rowIndex, group);
    const belowMinimum =
      group.minQuantity > 0 && totalSelected < group.minQuantity;
    const aboveMaximum =
      group.maxQuantity > 0 && totalSelected > group.maxQuantity;

    return {
      invalid: belowMinimum || aboveMaximum,
      belowMinimum,
      aboveMaximum,
      totalSelected,
    };
  };

  const renderGroupLimitDescription = (group: ProductAdditional) => {
    if (group.minQuantity === 0 && group.maxQuantity === 0) {
      return "Selecione livremente os adicionais deste grupo.";
    }

    if (group.minQuantity > 0 && group.maxQuantity > 0) {
      if (group.minQuantity === group.maxQuantity) {
        return `Selecione exatamente ${group.minQuantity} item${
          group.minQuantity !== 1 ? "s" : ""
        }.`;
      }
      return `Selecione entre ${group.minQuantity} e ${group.maxQuantity} item${
        group.maxQuantity !== 1 ? "s" : ""
      }.`;
    }

    if (group.minQuantity > 0) {
      return `Selecione pelo menos ${group.minQuantity} item${
        group.minQuantity !== 1 ? "s" : ""
      }.`;
    }

    if (group.maxQuantity > 0) {
      return `Selecione até ${group.maxQuantity} item${
        group.maxQuantity !== 1 ? "s" : ""
      }.`;
    }

    return "Selecione os adicionais desejados.";
  };

  const renderAdditionals = (product: ProductDetails) => {
    if (!product.additional || product.additional.length === 0) {
      return null;
    }

    const isCollapsed = collapsedAdditionals[product.rowKey] ?? false;
    const selectedAdditionals = product.additionals ?? [];
    const totalSelectedAdditionals = selectedAdditionals.reduce(
      (sum, additional) => sum + additional.quantity,
      0,
    );

    const toggleAdditionalsSection = () => {
      setCollapsedAdditionals((prev) => ({
        ...prev,
        [product.rowKey]: !isCollapsed,
      }));
    };

    return (
      <div className="mt-4 rounded-xl border border-orange-200 bg-orange-50/40">
        <div className="flex flex-col gap-3 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-[#EE7B11]">
              <Sparkles className="h-4 w-4" />
              <span className="text-sm font-medium">Adicionais</span>
              {totalSelectedAdditionals > 0 && (
                <Badge
                  variant="outline"
                  className="border-[#EE7B11]/30 text-xs text-[#EE7B11]"
                >
                  {totalSelectedAdditionals} selecionado
                  {totalSelectedAdditionals > 1 ? "s" : ""}
                </Badge>
              )}
            </div>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={toggleAdditionalsSection}
              className="flex items-center gap-2 self-start rounded-full text-[#EE7B11] hover:bg-[#EE7B11]/10"
            >
              {isCollapsed ? (
                <>
                  <ChevronDown className="h-4 w-4" />
                  <span className="text-sm font-medium">Expandir</span>
                </>
              ) : (
                <>
                  <ChevronUp className="h-4 w-4" />
                  <span className="text-sm font-medium">Recolher</span>
                </>
              )}
            </Button>
          </div>

          {selectedAdditionals.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedAdditionals.map((additional) => (
                <Badge
                  key={additional.itemId}
                  variant="outline"
                  className="bg-white text-xs text-[#EE7B11]"
                >
                  {additional.label} × {additional.quantity}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {!isCollapsed && (
          <div className="space-y-4 border-t border-orange-100 p-4 pt-0">
            {product.additional.map((group) => {
              const { invalid, belowMinimum, totalSelected } =
                isGroupSelectionInvalid(product.rowIndex, group);

              const maxQuantity = group.maxQuantity;

                const renderRadioGroup = () => {
                  const selectedItem = group.items.find(
                    (item) =>
                      getAdditionalQuantity(product.rowIndex, item.id || "") > 0,
                  );

                return (
                  <RadioGroup
                    value={selectedItem?.id ?? ""}
                    onValueChange={(value) =>
                      handleRadioSelection(
                        product.rowIndex,
                        group,
                        value ? value : null,
                      )
                    }
                    className="grid gap-3 md:grid-cols-2"
                  >
                    {group.items.map((item) => {
                      const isSelected = selectedItem?.id === item.id;
                      const itemId = `${product.rowKey}-${group.id}-radio-${item.id}`;

                      return (
                        <Label
                          key={item.id}
                          htmlFor={itemId}
                          className={cn(
                            "flex cursor-pointer flex-col gap-2 rounded-lg border p-3 transition hover:border-[#EE7B11]",
                            isSelected
                              ? "border-[#EE7B11] bg-orange-50"
                              : "border-gray-200 bg-white",
                          )}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {item.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {numberToMoney(item.unitPrice)}
                              </p>
                            </div>
                            <RadioGroupItem
                              id={itemId}
                              value={item.id ?? ""}
                              className="mt-1"
                            />
                          </div>

                          {item.unitCost !== undefined && (
                            <p className="text-xs text-gray-400">
                              Custo: {numberToMoney(item.unitCost)}
                            </p>
                          )}
                        </Label>
                      );
                    })}
                  </RadioGroup>
                );
              };

              const renderCheckboxGroup = () => {
                return (
                  <div className="space-y-2">
                    {group.items.map((item) => {
                      const quantity = getAdditionalQuantity(
                        product.rowIndex,
                        item.id || "",
                      );
                      const checked = quantity > 0;
                      const itemId = `${product.rowKey}-${group.id}-checkbox-${item.id}`;

                      return (
                        <div
                          key={item.id}
                          className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white p-3 hover:border-[#EE7B11]"
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox
                              id={itemId}
                              checked={checked}
                              onCheckedChange={(value) =>
                                handleCheckboxToggle(
                                  product.rowIndex,
                                  group,
                                  item,
                                  Boolean(value),
                                )
                              }
                            />
                            <Label
                              htmlFor={itemId}
                              className="flex flex-col gap-1"
                            >
                              <span className="text-sm font-medium text-gray-900">
                                {item.name}
                              </span>
                              <span className="text-xs text-gray-500">
                                {numberToMoney(item.unitPrice)}
                              </span>
                            </Label>
                          </div>

                          {checked && (
                            <Badge variant="secondary" className="text-xs">
                              Selecionado
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              };

              const renderQuantityGroup = () => {
                return (
                  <div className="space-y-3">
                    {group.items.map((item) => {
                      const quantity = getAdditionalQuantity(
                        product.rowIndex,
                        item.id || "",
                      );

                      return (
                        <div
                          key={item.id}
                          className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-3"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {item.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {numberToMoney(item.unitPrice)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                size="icon"
                                variant="outline"
                                className="h-8 w-8"
                                onClick={() =>
                                  handleNumberSelection(
                                    product.rowIndex,
                                    group,
                                    item,
                                    quantity - 1,
                                  )
                                }
                                disabled={quantity <= 0}
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                              <Input
                                type="number"
                                min={0}
                                value={quantity}
                                onChange={(event) => {
                                  const raw = Number(event.target.value);
                                  if (Number.isNaN(raw)) return;
                                  handleNumberSelection(
                                    product.rowIndex,
                                    group,
                                    item,
                                    raw,
                                  );
                                }}
                                className="h-8 w-16 text-center"
                              />
                              <Button
                                type="button"
                                size="icon"
                                variant="outline"
                                className="h-8 w-8"
                                onClick={() =>
                                  handleNumberSelection(
                                    product.rowIndex,
                                    group,
                                    item,
                                    quantity + 1,
                                  )
                                }
                                disabled={
                                  maxQuantity > 0 && totalSelected >= maxQuantity
                                }
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              };

              return (
                <div
                  key={group.id ?? group.name}
                  className="space-y-3 rounded-xl border border-orange-100 bg-white p-4 shadow-sm"
                >
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-gray-900">
                        {group.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {renderGroupLimitDescription(group)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-orange-50 text-xs">
                        Selecionados: {totalSelected}
                        {group.maxQuantity > 0 ? `/${group.maxQuantity}` : ""}
                      </Badge>
                    </div>
                  </div>

                  {invalid && (
                    <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      <span>
                        {belowMinimum
                          ? `Selecione pelo menos ${group.minQuantity} item${
                              group.minQuantity !== 1 ? "s" : ""
                            }.`
                          : `Seleção máxima de ${group.maxQuantity} item${
                              group.maxQuantity !== 1 ? "s" : ""
                            }.`}
                      </span>
                    </div>
                  )}

                  <div className="space-y-3">
                    {group.selectionType === AdditionalSelectionType.RADIO &&
                      renderRadioGroup()}
                    {group.selectionType === AdditionalSelectionType.CHECKBOX &&
                      renderCheckboxGroup()}
                    {group.selectionType === AdditionalSelectionType.NUMBER &&
                      renderQuantityGroup()}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const getProductMargin = (product: ProductDetails) => {
    if (!showMarginInfo) return null;

    const unitPrice = product.unitPrice || 0;
    const unitCost = product.unitCost || 0;
    const margin = unitPrice - unitCost;
    const marginPercentage = unitPrice > 0 ? (margin / unitPrice) * 100 : 0;

    return {
      percentage: `${marginPercentage.toFixed(1)}%`,
      value: numberToMoney(margin),
      isPositive: margin >= 0,
    };
  };

  const getProductTotal = (product: ProductDetails) => {
    return numberToMoney((product.unitPrice || 0) * product.quantity);
  };

  const availableProducts = products;

  return (
    <div className="space-y-4">
      {/* Product Selector */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between h-12 text-left"
          >
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-gray-500" />
              <span className="text-gray-500">{placeholder}</span>
            </div>
            <Plus className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput
              placeholder="Buscar produtos..."
              value={searchValue}
              onValueChange={setSearchValue}
            />
            <CommandList>
              <CommandEmpty>
                {loading ? "Carregando..." : "Nenhum produto encontrado."}
              </CommandEmpty>
              <CommandGroup>
                {availableProducts.map((product) => (
                  <CommandItem
                    key={product.id}
                    onSelect={() => handleSelectProduct(product)}
                    className="flex items-center justify-between p-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center">
                        {product.file ? (
                          <img
                            src={
                              typeof product.file === "string"
                                ? product.file
                                : product.file.url
                            }
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Package className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium">{product.name}</div>
                        {showFinancialInfo && (
                          <div className="text-sm text-gray-500">
                            Preço: {numberToMoney(product.unitPrice)}
                            {product.unitCost && (
                              <> | Custo: {numberToMoney(product.unitCost)}</>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <Plus className="h-4 w-4" />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected Products with Badges */}
      {showBadges && value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedProducts.map((product) => (
            <Badge
              key={product.rowKey}
              variant="secondary"
              className="flex items-center gap-2 py-1 px-3"
            >
              <span>{product.name}</span>
              <span className="text-xs text-gray-500">
                ({product.quantity}x)
              </span>
              <button
                type="button"
                onClick={() =>
                  handleRemoveProduct(product.rowIndex, product.rowKey)
                }
                className="ml-1 hover:text-red-500 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Detailed Product List */}
      {selectedProducts.length > 0 && (
        <>
          {/* Desktop Layout */}
          <div className="hidden md:block">
            <Card>
              <CardContent className="p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Produtos Selecionados
                </h4>

                <div className="space-y-3">
                  {selectedProducts.map((product) => {
                    const margin = getProductMargin(product);

                    const hasAdditionals =
                      product.additionals && product.additionals.length > 0;

                    return (
                      <div
                        key={product.rowKey}
                        className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                      >
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                          <div className="flex items-start gap-3 lg:flex-1">
                            <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-md bg-gray-100">
                              {product.file ? (
                                <img
                                  src={
                                    typeof product.file === "string"
                                      ? product.file
                                      : product.file.url
                                  }
                                  alt={product.name}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <Package className="h-6 w-6 text-gray-400" />
                              )}
                            </div>
                            <div className="space-y-2">
                              <div>
                                <div className="font-medium text-gray-900">
                                  {product.name}
                                </div>
                                {showFinancialInfo && (
                                  <div className="text-sm text-gray-600">
                                    Preço:{" "}
                                    <span className="font-medium text-green-600">
                                      {numberToMoney(product.unitPrice)}
                                    </span>
                                    {product.unitCost && (
                                      <>
                                        {" | "}
                                        Custo:{" "}
                                        <span className="font-medium text-gray-800">
                                          {numberToMoney(product.unitCost)}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>

                              {margin && (
                                <div className="text-sm">
                                  Margem:{" "}
                                  <span
                                    className={`font-medium ${
                                      margin.isPositive
                                        ? "text-green-600"
                                        : "text-red-600"
                                    }`}
                                  >
                                    {margin.percentage} ({margin.value})
                                  </span>
                                </div>
                              )}

                              {hasAdditionals && (
                                <div className="flex flex-wrap gap-2 pt-1">
                                  {product.additionals?.map((additional) => (
                                    <Badge
                                      key={additional.itemId}
                                      variant="outline"
                                      className="bg-orange-50 text-xs text-[#EE7B11]"
                                    >
                                      {additional.label} × {additional.quantity}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-9 w-9"
                                onClick={() =>
                                  handleQuantityChange(
                                    product.rowIndex,
                                    product.rowKey,
                                    product.quantity - 1
                                  )
                                }
                                disabled={product.quantity <= 1}
                              >
                                <Minus className="h-3 w-3" />
                              </Button>

                              <Input
                                type="number"
                                min="1"
                                inputMode="numeric"
                                value={
                                  quantityInputs[product.rowKey] ??
                                  String(product.quantity)
                                }
                                onChange={(e) => {
                                  const rawValue = e.target.value;
                                  if (/^\d*$/.test(rawValue)) {
                                    setQuantityInputs((prev) => ({
                                      ...prev,
                                      [product.rowKey]: rawValue,
                                    }));

                                    if (rawValue !== "") {
                                      const parsed = parseInt(rawValue, 10);
                                      if (!Number.isNaN(parsed) && parsed > 0) {
                                        handleQuantityChange(
                                          product.rowIndex,
                                          product.rowKey,
                                          parsed
                                        );
                                      }
                                    }
                                  }
                                }}
                                onBlur={() => {
                                  const rawValue = quantityInputs[product.rowKey];
                                  const parsed = rawValue
                                    ? parseInt(rawValue, 10)
                                    : NaN;
                                  const normalized =
                                    !Number.isNaN(parsed) && parsed > 0
                                      ? parsed
                                      : 1;

                                  if (normalized !== product.quantity) {
                                    handleQuantityChange(
                                      product.rowIndex,
                                      product.rowKey,
                                      normalized
                                    );
                                  } else if (rawValue !== String(normalized)) {
                                    setQuantityInputs((prev) => ({
                                      ...prev,
                                      [product.rowKey]: String(normalized),
                                    }));
                                  }
                                }}
                                className="h-9 w-20 text-center"
                              />

                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-9 w-9"
                                onClick={() =>
                                  handleQuantityChange(
                                    product.rowIndex,
                                    product.rowKey,
                                    product.quantity + 1
                                  )
                                }
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>

                            {showFinancialInfo && (
                              <div className="flex flex-col items-center justify-center rounded-md border border-orange-100 bg-orange-50 px-3 py-2 text-center">
                                <span className="text-xs text-gray-500">Total</span>
                                <span className="font-semibold text-[#EE7B11]">
                                  {getProductTotal(product)}
                                </span>
                              </div>
                            )}

                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 text-red-500 hover:bg-red-50 hover:text-red-600"
                              onClick={() =>
                                handleRemoveProduct(
                                  product.rowIndex,
                                  product.rowKey
                                )
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {renderAdditionals(product)}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden space-y-3">
            {selectedProducts.map((product) => {
              const margin = getProductMargin(product);
              const hasAdditionals =
                product.additionals && product.additionals.length > 0;

              return (
                <Card key={product.rowKey} className="border shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex flex-1 gap-3">
                          <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-md bg-gray-100">
                            {product.file ? (
                              <img
                                src={
                                  typeof product.file === "string"
                                    ? product.file
                                    : product.file.url
                                }
                                alt={product.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <Package className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                          <div className="space-y-1">
                            <div className="text-sm font-semibold text-gray-900">
                              {product.name}
                            </div>
                            {showFinancialInfo && (
                              <div className="text-xs text-gray-600">
                                Preço: {numberToMoney(product.unitPrice)}
                              </div>
                            )}
                            {margin && (
                              <div className="text-xs">
                                Margem:{" "}
                                <span
                                  className={`font-medium ${
                                    margin.isPositive
                                      ? "text-green-600"
                                      : "text-red-600"
                                  }`}
                                >
                                  {margin.percentage}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:bg-red-50 hover:text-red-600"
                          onClick={() =>
                            handleRemoveProduct(product.rowIndex, product.rowKey)
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      {hasAdditionals && (
                        <div className="flex flex-wrap gap-2">
                          {product.additionals?.map((additional) => (
                            <Badge
                              key={additional.itemId}
                              variant="outline"
                              className="bg-orange-50 text-[11px] text-[#EE7B11]"
                            >
                              {additional.label} × {additional.quantity}
                            </Badge>
                          ))}
                        </div>
                      )}

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-700">
                            Quantidade
                          </label>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() =>
                                handleQuantityChange(
                                  product.rowIndex,
                                  product.rowKey,
                                  product.quantity - 1
                                )
                              }
                              disabled={product.quantity <= 1}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>

                            <Input
                              type="number"
                              min="1"
                              inputMode="numeric"
                              value={
                                quantityInputs[product.rowKey] ??
                                String(product.quantity)
                              }
                              onChange={(e) => {
                                const rawValue = e.target.value;
                                if (/^\\d*$/.test(rawValue)) {
                                  setQuantityInputs((prev) => ({
                                    ...prev,
                                    [product.rowKey]: rawValue,
                                  }));

                                  if (rawValue !== "") {
                                    const parsed = parseInt(rawValue, 10);
                                    if (!Number.isNaN(parsed) && parsed > 0) {
                                      handleQuantityChange(
                                        product.rowIndex,
                                        product.rowKey,
                                        parsed
                                      );
                                    }
                                  }
                                }
                              }}
                              onBlur={() => {
                                const rawValue = quantityInputs[product.rowKey];
                                const parsed = rawValue
                                  ? parseInt(rawValue, 10)
                                  : NaN;
                                const normalized =
                                  !Number.isNaN(parsed) && parsed > 0
                                    ? parsed
                                    : 1;

                                if (normalized !== product.quantity) {
                                  handleQuantityChange(
                                    product.rowIndex,
                                    product.rowKey,
                                    normalized
                                  );
                                } else if (rawValue !== String(normalized)) {
                                  setQuantityInputs((prev) => ({
                                    ...prev,
                                    [product.rowKey]: String(normalized),
                                  }));
                                }
                              }}
                              className="h-8 flex-1 text-center"
                            />

                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() =>
                                handleQuantityChange(
                                  product.rowIndex,
                                  product.rowKey,
                                  product.quantity + 1
                                )
                              }
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>

                        {showFinancialInfo && (
                          <div className="rounded-md border border-orange-100 bg-orange-50 px-3 py-2 text-center">
                            <div className="text-xs text-gray-500">Total</div>
                            <div className="text-sm font-semibold text-[#EE7B11]">
                              {getProductTotal(product)}
                            </div>
                          </div>
                        )}
                      </div>

                      {margin && (
                        <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                          Margem:
                          <span
                            className={`ml-1 font-medium ${
                              margin.isPositive
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            {margin.percentage} ({margin.value})
                          </span>
                        </div>
                      )}

                      {renderAdditionals(product)}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
